import { Chart } from 'primereact/chart';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { formatDate3 } from '../../utils/dateFormat';
import { metricValueFormat, metricValueFormatWithoutUnit } from '../../utils/metricValueFormat';
import { Numbers, monthNames } from '../../utils/typeConstants';
import { getExceedsTarget } from '../../utils/metricAttainment';
import { DateTemplate } from '../dateTemplate/DateTemplate';
import { MetricTimeProps } from '../metricTimeSeriesBarCard/type';
import { TeamMetricView } from '../teamMetricView/TeamMetricView';
import './metricTimeSeriesBarGraph.scss';
import { MetricColors, MetricZoneBackgroundColors, MetricTrendTierColors } from '../metricMonthlySummaryCard/GradientDefs';

// Pure helper — no closure deps, defined once at module level
const isZero = (value: number): boolean => value === 0;

// Shared datalabels config — white text with dark stroke/shadow for contrast on any bar color
const barDatalabels = {
    color: '#FFFFFF',
    textStrokeColor: 'rgba(0,0,0,0.8)',
    textStrokeWidth: 3,
    textShadowBlur: 4,
    textShadowColor: 'rgba(0,0,0,0.6)',
    anchor(context: { dataset: { data: number[] }; dataIndex: number }) {
        return context.dataset.data[context.dataIndex] > 0 ? 'end' : 'start';
    },
    align(context: { dataset: { data: number[] }; dataIndex: number }) {
        return context.dataset.data[context.dataIndex] > 0 ? 'start' : 'end';
    },
    offset(context: { dataset: { data: number[] }; dataIndex: number }) {
        return context.dataset.data[context.dataIndex] > 0 ? 5 : -10;
    },
    font: { weight: 'bold', size: 12 },
};

const zoneBackgroundPlugin = {
    id: 'zoneBackground',
    beforeDatasetsDraw(chart: any) {
        const meetsDataset = chart.data.datasets.find((d: any) => d.zoneColors);
        if (!meetsDataset?.isTieredMode) return;
        const exceedsDataset = chart.data.datasets.find((d: any) => d.label === 'Exceeds');
        if (!exceedsDataset) return;

        const { ctx, chartArea, scales } = chart;
        const xScale = scales.x;
        const yScale = scales.y;
        const { zoneColors } = meetsDataset;
        const n = meetsDataset.data.length;
        if (n === 0) return;

        const xPixel = (i: number) => {
            if (i === 0) return chartArea.left;
            if (i === n - 1) return chartArea.right;
            return xScale.getPixelForValue(i);
        };

        const meetsY = meetsDataset.data.map((v: number) => yScale.getPixelForValue(Number(v)));
        const exceedsY = exceedsDataset.data.map((v: number | null, i: number) =>
            v == null ? meetsY[i] : yScale.getPixelForValue(Number(v))
        );
        const ceilingY = new Array(n).fill(chartArea.top);
        const floorY = new Array(n).fill(chartArea.bottom);

        const upperBoundY = meetsY.map((y: number, i: number) => Math.min(y, exceedsY[i])); // nearer ceiling
        const lowerBoundY = meetsY.map((y: number, i: number) => Math.max(y, exceedsY[i])); // nearer floor

        const fillBand = (topYs: number[], bottomYs: number[], color: string) => {
            ctx.save();
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(xPixel(0), topYs[0]);
            for (let i = 1; i < n; i++) ctx.lineTo(xPixel(i), topYs[i]);
            for (let i = n - 1; i >= 0; i--) ctx.lineTo(xPixel(i), bottomYs[i]);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        };

        fillBand(upperBoundY, lowerBoundY, zoneColors.meetsToExceeds);
        fillBand(lowerBoundY, floorY, zoneColors.start);
        fillBand(ceilingY, upperBoundY, zoneColors.beyondExceeds);
    },
};

// Draws the Target/Meets/Exceeds reference lines edge-to-edge across the full plot width,
// independent of the x-axis category `offset`. Those lines are plotted at the same category
// positions as the bars, so once the bars' offset:true padding was restored (see basicOptions),
// the lines - anchored at category centers - started and ended half a slot in from the plot

const edgeToEdgeReferenceLinePlugin = {
    id: 'edgeToEdgeReferenceLine',
    beforeDatasetsDraw(chart: any) {
        const { ctx, chartArea, scales } = chart;
        chart.data.datasets.forEach((dataset: any) => {
            if (!dataset.drawFullWidth) return;
            const value = dataset.data?.[0];
            if (value == null) return;
            const y = scales.y.getPixelForValue(Number(value));
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash(dataset.borderDash ?? []);
            ctx.strokeStyle = dataset.referenceLineColor;
            ctx.lineWidth = dataset.referenceLineWidth ?? 2;
            ctx.moveTo(chartArea.left, y);
            ctx.lineTo(chartArea.right, y);
            ctx.stroke();
            ctx.restore();
        });
    },
};

export const MetricTimeSeriesBarGraph = memo(({
    metricName,
    metricId,
    scorecardVersionId,
    employeeNumber,
    target,
    metricTypeName,
    dailyData,
    metricDirectionId,
    calculatedDate,
    metricHistoryData,
    fromMetricDashboard,
    metricIndex = 0,
    metricOmitted,
    currentMonthLabel = 'Current Month',
    historyLabel = 'History',
    exceedsTarget,
}: MetricTimeProps) => {
    const [basicData, setBasicData] = useState<any>();
    const [isCurrentMonth, setIsCurrentMonth] = useState(fromMetricDashboard);
    const [gYear, setGYear] = useState<number>(0);
    const [gMonth, setGMonth] = useState<number>(0);
    const [gResultDate, setGResultDate] = useState<Date | ''>();
    const [displayModal, setDisplayModal] = useState(false);

    // Derive color values from props — no useState, no extra render cycle
    const colorIndex = metricOmitted ? 5 : (metricIndex ?? 0);
    const metricColorConfig = useMemo(
        () => MetricColors[colorIndex] ?? MetricColors[0],
        [colorIndex]
    );
    const textColor = metricColorConfig.textColor;
    const selectedMetricBackgrndColor = metricColorConfig.bgColor;
    const trendTierIndex = metricOmitted ? 6 : (metricIndex ?? 0);
    const tieredColorPair = useMemo(
        () => MetricTrendTierColors[trendTierIndex] ?? MetricTrendTierColors[0],
        [trendTierIndex]
    );
    const meetsLineColor = tieredColorPair.meets;
    const exceedsLineColor = tieredColorPair.exceeds;
    // Flat background fills for the three chart zones (start->Meets, Meets->Exceeds,
    // Exceeds->edge) - same trendTierIndex as the line colors above so a metric's shaded zones
    // and its reference lines always draw from the same palette.
    const zoneColors = useMemo(
        () => MetricZoneBackgroundColors[trendTierIndex] ?? MetricZoneBackgroundColors[0],
        [trendTierIndex]
    );

    const handleClick = useCallback((event: unknown, elements: { index: number }[]) => {
        if (!fromMetricDashboard && elements.length > 0) {
            const dataIndex = elements[0].index;
            const month = !isCurrentMonth ? basicData?.datasets[0]?.month?.[dataIndex] : 0;
            const year = !isCurrentMonth ? basicData?.datasets[0]?.year?.[dataIndex] : 0;
            const resultDate = isCurrentMonth ? basicData?.datasets[0]?.resultDate?.[dataIndex] : '';
            setGYear(year);
            setGMonth(month);
            setGResultDate(resultDate);
            setDisplayModal(true);
        }
    }, [fromMetricDashboard, isCurrentMonth, basicData]);

    const basicOptions = useMemo(() => ({
        maintainAspectRatio: false,
        aspectRatio: 2.0,
        interaction: {
            mode: 'nearest',
            intersect: true,
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: true,
                position: 'nearest',
                callbacks: {
                    label: (ctx: { dataset: { label: string }; dataIndex: number; formattedValue: string; raw: any }) => {
                        if (ctx.dataset.label === 'Target' || ctx.dataset.label === 'Exceeds' || ctx.dataset.label === 'Meets') {
                            return `${ctx.dataset.label}: ${ctx.formattedValue}%`;
                        }
                        if (ctx.raw == null) return '--';
                        return ctx.raw === 0 ? '0' : ctx.formattedValue;
                    },
                },
            },
        },
        onClick: handleClick,
        scales: {
            x: {
                display: true,
                min: 0,
                // offset:true (the bar-chart default for a category scale) reserves half a
                // category-slot of padding on each edge of the plot area. Without it, the
                // first/last category sits exactly on the plot edge, so the bar centered on
                // that position gets clipped to half its width - which is the bug this restores.
                offset: true,
                ticks: { color: '#495057' },
                grid: { display: false },
            },
            y: {
                type: 'linear',
                color: textColor,
                display: true,
                position: 'left',
                grid: { display: false },
            },
        },
    }), [handleClick]);

    useEffect(() => {
        const isBetterOrEqual = (value: number, threshold: number): boolean =>
            metricDirectionId === 2 ? value <= threshold : value >= threshold;
        const getBarAttainment = (
            item: { metric_value?: number | null; metric_attained?: number | boolean },
            meetsValue: number | null,
            exceedsValue: number | null,
            tieredMode: boolean
        ): { color: string; status: string | null } => {
            const rawValue = item?.metric_value == null ? null : Number(item.metric_value);
            if (tieredMode && rawValue != null) {
                const value = Number(metricValueFormatWithoutUnit(rawValue, metricTypeName));
                const meetsRounded = meetsValue != null ? Number(metricValueFormatWithoutUnit(meetsValue, metricTypeName)) : null;
                const exceedsRounded = exceedsValue != null ? Number(metricValueFormatWithoutUnit(exceedsValue, metricTypeName)) : null;
                if (exceedsRounded != null && isBetterOrEqual(value, exceedsRounded)) {
                    return { color: tieredColorPair.exceeds, status: 'Exceeds' };
                }
                if (meetsRounded != null && isBetterOrEqual(value, meetsRounded)) {
                    return { color: tieredColorPair.meets, status: 'Meets' };
                }
                return { color: tieredColorPair.belowMeets, status: 'Below Meets' };
            }
            const legacyColor = (item.metric_attained === 1 || item.metric_attained === true)
                ? metricColorConfig?.metricMet
                : metricColorConfig?.metricNotMet;
            return { color: legacyColor, status: null };
        };

        if (dailyData?.length > 0 && isCurrentMonth) {
            const reversedData = dailyData.slice().reverse();
            const metricValues = reversedData.map((item) =>
                item?.metric_value == null ? null : Number(metricValueFormatWithoutUnit(item.metric_value, metricTypeName))
            );

            const isTieredMode = exceedsTarget != null;
            const meetsLineData = new Array(reversedData.length).fill(
                metricValueFormatWithoutUnit(Number(target), metricTypeName)
            );

            const datasets: any[] = [
                {
                    data: meetsLineData,
                    type: 'line',
                    label: isTieredMode ? 'Meets' : 'Target',
                    borderColor: 'transparent',
                    referenceLineColor: meetsLineColor,
                    borderWidth: 2,
                    referenceLineWidth: 2,
                    drawFullWidth: true,
                    borderDash: [5, 5],
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointHitRadius: 15,
                    resultDate: reversedData.map((item) => item.result_date),
                    datalabels: { display: false },
                    fill: false,
                    zoneColors,
                    metricDirectionId,
                    isTieredMode,
                },
                {
                    data: metricValues,
                    fill: false,
                    backgroundColor: reversedData.map((item) =>
                        getBarAttainment(item, Number(target), exceedsTarget != null ? Number(exceedsTarget) : null, isTieredMode).color
                    ),
                    hoverBackgroundColor: reversedData.map((item) =>
                        getBarAttainment(item, Number(target), exceedsTarget != null ? Number(exceedsTarget) : null, isTieredMode).color
                    ),
                    borderWidth: Numbers.ONE,
                    borderRadius: Numbers.FIVE,
                    datalabels: barDatalabels,
                },
            ];

            if (isTieredMode) {
                datasets.push({
                    data: new Array(reversedData.length).fill(
                        metricValueFormatWithoutUnit(Number(exceedsTarget), metricTypeName)
                    ),
                    type: 'line',
                    label: 'Exceeds',
                    borderColor: 'transparent',
                    referenceLineColor: exceedsLineColor,
                    borderWidth: 2,
                    referenceLineWidth: 2,
                    drawFullWidth: true,
                    borderDash: [2, 3],
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointHitRadius: 15,
                    datalabels: { display: false },
                    fill: false,
                });
            }

            setBasicData({
                labels: reversedData.map((item) => {
                    const resultDate = new Date(item.result_date);
                    return `${resultDate.getDate()}-${resultDate.toLocaleString('en-US', { month: 'short' })}`;
                }),
                datasets,
            });
        } else if (!isCurrentMonth && metricHistoryData?.length > 0) {
            const allHistoryData = metricHistoryData.reduce((acc: any[], item: any) => {
                return [...acc, ...(item.metricsHistory || [])];
            }, []);

            // Sort by year and month to ensure chronological order
            const sortedData = allHistoryData.sort((a: any, b: any) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.month - b.month;
            });

            const reversedData = sortedData.slice().reverse();
            const metricValues = reversedData.map((item: any) =>
                item?.metric_value == null ? null : Number(metricValueFormatWithoutUnit(item.metric_value, metricTypeName))
            );

            const rowExceedsTargets = reversedData.map((item: any) =>
                getExceedsTarget(item.targets) ?? exceedsTarget ?? null
            );
            const isTieredMode = rowExceedsTargets.some((v: number | null) => v != null);
            const meetsLineData = reversedData.map((item: any) =>
                metricValueFormatWithoutUnit(item.target, metricTypeName)
            );

            const datasets: any[] = [
                {
                    data: meetsLineData,
                    type: 'line',
                    label: isTieredMode ? 'Meets' : 'Target',
                    borderColor: 'transparent',
                    referenceLineColor: meetsLineColor,
                    borderWidth: 2,
                    referenceLineWidth: 2,
                    drawFullWidth: true,
                    borderDash: [5, 5],
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointHitRadius: 15,
                    tension: 0.4,
                    month: reversedData.map((item: any) => item.month),
                    year: reversedData.map((item: any) => item.year),
                    datalabels: { display: false },
                    fill: false,
                    zoneColors,
                    metricDirectionId,
                    isTieredMode,
                },
                {
                    data: metricValues,
                    fill: false,
                    backgroundColor: reversedData.map((item: any, i: number) =>
                        getBarAttainment(item, Number(item.target), rowExceedsTargets[i] != null ? Number(rowExceedsTargets[i]) : null, isTieredMode).color
                    ),
                    hoverBackgroundColor: reversedData.map((item: any, i: number) =>
                        getBarAttainment(item, Number(item.target), rowExceedsTargets[i] != null ? Number(rowExceedsTargets[i]) : null, isTieredMode).color
                    ),
                    borderWidth: Numbers.ONE,
                    borderRadius: Numbers.FIVE,
                    datalabels: barDatalabels,
                },
            ];

            if (isTieredMode) {
                datasets.push({
                    data: rowExceedsTargets.map((v: number | null) =>
                        v != null ? metricValueFormatWithoutUnit(v, metricTypeName) : null
                    ),
                    type: 'line',
                    label: 'Exceeds',
                    borderColor: 'transparent',
                    referenceLineColor: exceedsLineColor,
                    borderWidth: 2,
                    referenceLineWidth: 2,
                    drawFullWidth: true,
                    borderDash: [2, 3],
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    pointHitRadius: 15,
                    tension: 0.4,
                    datalabels: { display: false },
                    fill: false,
                });
            }

            setBasicData({
                labels: reversedData.map((item: any) => `${item?.date}`),
                datasets,
            });
        }
    }, [dailyData, metricTypeName, target, metricHistoryData, isCurrentMonth, metricColorConfig, exceedsTarget, meetsLineColor, exceedsLineColor, zoneColors, metricDirectionId, tieredColorPair]);

    const toggleView = useCallback(() => {
        setIsCurrentMonth((prev) => !prev);
    }, []);

    const hideModal = useCallback(() => {
        setDisplayModal(false);
    }, []);

    const renderHeader = useCallback(() => (
        <div className="flex-between">
            <div>
                <h3>
                    Team Stats - {monthNames[gMonth]}{' '}
                    {gYear > 0 ? gYear : formatDate3(gResultDate?.toLocaleString() ?? '')}
                </h3>
            </div>
        </div>
    ), [gMonth, gYear, gResultDate]);

    const renderTriadModal = useCallback(() => (
        <Dialog
            header={renderHeader()}
            visible={displayModal}
            onHide={hideModal}
            style={{ width: '80%', maxWidth: '800px', margin: '0 auto' }}
            className="card shadow bg-body rounded mt-4 border-0 p-3 w-65"
            modal
        >
            <TeamMetricView
                metricId={metricId}
                metricName={metricName}
                scorecardVersionId={scorecardVersionId}
                employeeNumber={employeeNumber}
                month={gMonth}
                year={gYear}
                resultDate={gResultDate ?? new Date()}
                metricDirectionId={metricDirectionId}
            />
        </Dialog>
    ), [
        renderHeader,
        displayModal,
        hideModal,
        metricId,
        metricName,
        scorecardVersionId,
        employeeNumber,
        gMonth,
        gYear,
        gResultDate,
        metricDirectionId,
    ]);

    const hasNoData = useMemo(
        () => basicData?.datasets[1]?.data.every(isZero),
        [basicData]
    );

    // Precomputed (not an inline ternary in JSX) - matches this file's if/else style elsewhere.
    const trendLegendContent = useMemo(() => {
        if (hasNoData) {
            return null;
        }
        if (exceedsTarget != null) {
            return (
                <div className="metricTrendLegend">
                    <span className="metricTrendLegend__item" style={{ color: meetsLineColor }}>
                        <span className="metricTrendLegend__swatch metricTrendLegend__swatch--meets" style={{ borderColor: meetsLineColor }} />
                        Meets: {metricValueFormat(target, metricTypeName)}
                    </span>
                    <span className="metricTrendLegend__item" style={{ color: exceedsLineColor }}>
                        <span className="metricTrendLegend__swatch metricTrendLegend__swatch--exceeds" style={{ borderColor: exceedsLineColor }} />
                        Exceeds: {metricValueFormat(exceedsTarget, metricTypeName)}
                    </span>
                </div>
            );
        }
        if (!isCurrentMonth) {
            return null;
        }
        return (
            <div className="metricTrendLegend">
                <span className="metricTrendLegend__item" style={{ color: meetsLineColor }}>
                    <span className="metricTrendLegend__swatch metricTrendLegend__swatch--meets" style={{ borderColor: meetsLineColor }} />
                    Target: {metricValueFormat(target, metricTypeName)}
                </span>
            </div>
        );
    }, [hasNoData, exceedsTarget, isCurrentMonth, meetsLineColor, exceedsLineColor, target, metricTypeName]);

    return (
        <div className="col">
            <div className="h-100">
                <div className="card-body" style={{ backgroundColor: selectedMetricBackgrndColor }}>
                    <div className="row d-flex flex-wrap">
                        <p className="mb-0" style={{ paddingRight: '30px', textAlign: 'right' }}>
                            {metricDirectionId === 2
                                ? 'Descending Metric (The goal is to be below Target Line)'
                                : 'Ascending Metric (The goal is to be above Target Line)'}
                        </p>
                        <div className="d-flex justify-content-between align-items-center mt-1">
                            <div className="d-flex align-items-center">
                                <span className="me-2 metricbargraph" style={{ color: textColor }}>
                                    {currentMonthLabel}
                                </span>
                                <InputSwitch
                                    id="CurrentHistorySwitch"
                                    checked={!isCurrentMonth}
                                    onChange={toggleView}
                                />
                                <span className="ms-2 metricbargraph" style={{ color: textColor }}>
                                    {historyLabel}
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-1">
                                {isCurrentMonth && (
                                    <p id="last-calculated" className="mb-0">
                                        Last calculated on{' '}
                                        {calculatedDate !== '' && calculatedDate !== undefined
                                            ? DateTemplate(calculatedDate)
                                            : ''}
                                    </p>
                                )}
                            </div>
                        </div>
                        {trendLegendContent && (
                            <div className="d-flex align-items-center mt-1">
                                {trendLegendContent}
                            </div>
                        )}
                        <div className="container mt-2">
                            {hasNoData ? (
                                <div className="border-0">
                                    <div className="align-middle text-center">
                                        <div className="row" style={{ height: '170px' }}>
                                            <div className="col-md-12" style={{ margin: 'auto' }}>
                                                <p className="align-middle">No Data Found</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <Chart
                                    key={isCurrentMonth ? 'current' : 'history'}
                                    type="bar"
                                    data={basicData}
                                    options={basicOptions}
                                    plugins={[zoneBackgroundPlugin, edgeToEdgeReferenceLinePlugin]}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {displayModal && renderTriadModal()}
        </div>
    );
});

MetricTimeSeriesBarGraph.displayName = 'MetricTimeSeriesBarGraph';
